// Per-user "pinned messages" — a private bookmark list staff keep for later ("something I
// need for later, for any reason"), surfaced via the Messages "Pinned" lens. Each pin
// carries a snapshot (preview/sender/time/thread title) captured at pin time so the list
// renders without having to load the message's thread.
//
// Account-bound: persisted via the server settings sync core (key "pinnedMessages", value
// { items: [...] }) so pins follow the user across devices. The pre-migration per-uid
// localStorage lists are not read (no seeding — accounts start empty); their remove-from-
// device purge line in profile-roster-store stays for old installs.

import {
  USER_SETTING_KEYS,
  getActiveSettingsUid,
  readUserSetting,
  subscribeUserSettingsActivation,
  writeUserSetting,
} from "@/lib/stores/user-settings-sync";

const PINNED_CHANGE_EVENT = "healo-pinned-messages-change";

// Pins snapshot the FULL message text as their preview and WhatsApp bodies can reach tens
// of KB; the Pinned lens renders line-clamped anyway, so cap the stored preview to keep
// worst-case pin lists far under the server's 512KB value budget.
const PIN_PREVIEW_MAX_CHARS = 2000;

// { uid, pins } — re-read from the sync core whenever the signed-in user changes.
let cache = null;

function pinKey(threadId, messageId) {
  return `${String(threadId || "").trim()}:${String(messageId || "").trim()}`;
}

function nowIso() {
  return new Date().toISOString();
}

function normalizePin(value) {
  if (!value || typeof value !== "object") return null;
  const threadId = String(value.threadId || "").trim();
  const messageId = String(value.messageId || "").trim();
  if (!threadId || !messageId) return null;
  return {
    threadId,
    messageId,
    scope: value.scope === "internal" ? "internal" : "normal",
    pinnedAt: String(value.pinnedAt || "").trim() || nowIso(),
    createdAt: String(value.createdAt || "").trim(),
    senderLabel: String(value.senderLabel || "").trim(),
    preview: String(value.preview || "").slice(0, PIN_PREVIEW_MAX_CHARS),
    threadTitle: String(value.threadTitle || "").trim(),
  };
}

function normalizePinList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const entry of value) {
    const pin = normalizePin(entry);
    if (!pin) continue;
    const key = pinKey(pin.threadId, pin.messageId);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(pin);
  }
  return result;
}

function readStoredPins() {
  const stored = readUserSetting(USER_SETTING_KEYS.PINNED_MESSAGES);
  return normalizePinList(stored?.items);
}

function loadPins() {
  ensureActivationSubscription();
  const uid = getActiveSettingsUid();
  if (cache && cache.uid === uid) return cache.pins;
  cache = { uid, pins: uid ? readStoredPins() : [] };
  return cache.pins;
}

// The cache re-reads the sync core lazily, but lazy re-reads alone never NOTIFY anyone:
// after a profile switch the Pinned lens would keep rendering the previous user's pins until
// some pin mutation happened. Invalidate + emit on every settings activation.
let activationSubscribed = false;
function ensureActivationSubscription() {
  if (activationSubscribed || typeof window === "undefined") return;
  activationSubscribed = true;
  subscribeUserSettingsActivation(() => {
    cache = null;
    emitChange();
  });
}

function persist(uid, pins) {
  if (!uid) return;
  writeUserSetting(USER_SETTING_KEYS.PINNED_MESSAGES, { items: pins });
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PINNED_CHANGE_EVENT));
}

function setPins(nextPins) {
  const uid = getActiveSettingsUid();
  const normalized = normalizePinList(nextPins);
  cache = { uid, pins: normalized };
  persist(uid, normalized);
  emitChange();
  return normalized;
}

// Most-recently-pinned first. ISO timestamps sort lexicographically by time.
function sortNewestFirst(pins) {
  return [...pins].sort((a, b) => String(b.pinnedAt).localeCompare(String(a.pinnedAt)));
}

export function getPinnedMessages() {
  return sortNewestFirst(loadPins());
}

export function getPinnedCount(scope) {
  const pins = loadPins();
  if (!scope) return pins.length;
  return pins.filter((p) => p.scope === scope).length;
}

export function isMessagePinned(threadId, messageId) {
  const key = pinKey(threadId, messageId);
  return loadPins().some((p) => pinKey(p.threadId, p.messageId) === key);
}

export function pinMessage(record) {
  const pin = normalizePin(record);
  if (!pin) return loadPins();
  const key = pinKey(pin.threadId, pin.messageId);
  const rest = loadPins().filter((p) => pinKey(p.threadId, p.messageId) !== key);
  return setPins([pin, ...rest]);
}

export function unpinMessage(threadId, messageId) {
  const key = pinKey(threadId, messageId);
  return setPins(loadPins().filter((p) => pinKey(p.threadId, p.messageId) !== key));
}

export function togglePinMessage(record) {
  const threadId = String(record?.threadId || "").trim();
  const messageId = String(record?.messageId || "").trim();
  if (!threadId || !messageId) return loadPins();
  if (isMessagePinned(threadId, messageId)) {
    return unpinMessage(threadId, messageId);
  }
  return pinMessage(record);
}

export function subscribePinnedMessages(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  ensureActivationSubscription();
  const handler = () => listener(getPinnedMessages());
  window.addEventListener(PINNED_CHANGE_EVENT, handler);
  return () => window.removeEventListener(PINNED_CHANGE_EVENT, handler);
}
